import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Components
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import DeleteWorkspaceModal from 'Shared/components/DeleteWorkspaceModal/DeleteWorkspaceModal';
import ExportWorkspaceModal from 'Shared/components/ExportWorkspaceModal/ExportWorkspaceModal';
import TransferWorkspaceModal from 'Shared/components/TransferWorkspaceModal/TransferWorkspaceModal';

type Props = {
  workspaceId: string;
};
const WorkspacePageTitleActionsDropdown = ({ workspaceId }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { isThirdPartyUser } = useCurrentUser();

  const [isDeleteModalOpen, toggleIsDeleteModalOpen] = useToggle(false);
  const [isExportModalOpen, toggleIsExportModalOpen] = useToggle(false);
  const [isTransferModalOpen, toggleIsTransferModalOpen] = useToggle(false);

  const { isPersonal, isOneOnOne, isTeamSync } = useWorkspace();

  const handleSettingsClick = useCallback(() => {
    history.push({
      pathname: PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId),
      state: { isModal: true, previousLocation: location },
    });
  }, [location]);

  const handleInviteClick = useCallback(() => {
    history.push({
      pathname: PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId),
      state: { isModal: true, previousLocation: location, view: 'invite' },
    });
  }, [location]);

  const handleMembersClick = useCallback(() => {
    history.push({
      pathname: PageUrlsUtil.makeWorkspaceSettingsUrl(workspaceId),
      state: { isModal: true, previousLocation: location, view: 'members' },
    });
  }, [location]);

  return (
    <>
      <WpDropdown placement="bottom-end">
        <WpDropdownToggle>
          <WpButton variant="tertiary" size="sm" qaTest="btn-workspace-page-header-dropdown-menu">
            <WpIcon name="ellipsis-h-alt" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          {!isOneOnOne && !isPersonal && (
            <>
              {isThirdPartyUser ? (
                <></>
              ) : (
                <WpDropdownMenuItem onClick={handleInviteClick} qaTest="workspace-header-dropdown-invite-option">
                  <WpIcon name="user-plus" />
                  Invite People {isThirdPartyUser}
                </WpDropdownMenuItem>
              )}
              <WpDropdownMenuItem onClick={handleMembersClick} qaTest="workspace-header-dropdown-members-option">
                <WpIcon name="user-friends" />
                View Members
              </WpDropdownMenuItem>
              <WpDropdownMenuDivider />
            </>
          )}
          {isOneOnOne && (
            <WpDropdownMenuItem onClick={toggleIsTransferModalOpen}>
              <WpIcon name="people-arrows" />
              Transfer workspace
            </WpDropdownMenuItem>
          )}
          <WpDropdownMenuItem onClick={toggleIsExportModalOpen}>
            <WpIcon name="file-export" />
            Export Activity
          </WpDropdownMenuItem>
          <WpDropdownMenuItem onClick={handleSettingsClick}>
            <WpIcon name="cog" size="sm" />
            Workspace Settings
          </WpDropdownMenuItem>
          {!isPersonal && !isTeamSync && (
            <>
              <WpDropdownMenuDivider />
              <WpDropdownMenuItem color="danger" onClick={toggleIsDeleteModalOpen}>
                <WpIcon name="trash" size="sm" />
                Delete workspace
              </WpDropdownMenuItem>
            </>
          )}
        </WpDropdownMenu>
      </WpDropdown>
      <DeleteWorkspaceModal workspaceId={workspaceId} isOpen={isDeleteModalOpen} onToggle={toggleIsDeleteModalOpen} />
      {isExportModalOpen && <ExportWorkspaceModal onToggle={toggleIsExportModalOpen} />}
      {isTransferModalOpen && <TransferWorkspaceModal onToggle={toggleIsTransferModalOpen} />}
    </>
  );
};

export default WorkspacePageTitleActionsDropdown;
